<script setup>
import { computed } from 'vue'
import { useRouter } from 'vue-router'
import PageHeader from '@/components/PageHeader.vue'

const router = useRouter()

// Computed properties
const userName = computed(() => mainStore.user.name || 'User')
const email = computed(() => mainStore.user.email || '')
const emailVerified = computed(() => !!mainStore.user.email_verified_at)

// Mask email: keep first and last char of local-part and domain intact, mask middle with asterisks
const maskedEmail = computed(() => {
  const e = email.value
  if (!e) return ''
  const parts = e.split('@')
  if (parts.length !== 2) return '*'.repeat(e.length)
  const [local, domain] = parts
  if (local.length <= 2) {
    return local[0] + '*'.repeat(Math.max(1, local.length - 1)) + '@' + domain
  }
  const middle = '*'.repeat(Math.max(1, local.length - 2))
  return `${local[0]}${middle}${local[local.length - 1]}@${domain}`
})

// Methods
const goToSettings = () => {
  router.push({ name: 'settings' })
}
</script>

<template>
  <v-container>
    <!-- Header -->
    <PageHeader :title="$t('profile.title')" icon="mdi-account-circle" />

    <v-row>
      <v-col cols="12">
        <!-- User Info Card -->
        <v-card elevation="2" class="mb-6">
          <v-card-title class="d-flex align-center">
            <v-icon color="primary" class="mr-3">mdi-account</v-icon>
            {{ $t('profile.userInfo.title') }}
          </v-card-title>
          
          <v-card-text>
            <v-row>
              <v-col cols="12">
                <v-row>
                  <v-col cols="12">
                    <div class="pa-4">
                      <div class="text-caption text--secondary">{{ $t('profile.userInfo.username') }}</div>
                      <div class="text-subtitle-1">{{ userName }}</div>
                    </div>
                  </v-col>

                  <v-col cols="12">
                    <div class="pa-4 d-flex align-center">
                      <div class="flex-grow-1">
                        <div class="text-caption text--secondary">{{ $t('profile.userInfo.email') }}</div>
                        <div class="text-subtitle-1">{{ maskedEmail }}</div>
                      </div>
                      <v-icon :color="emailVerified ? 'success' : 'warning'" class="ml-4">
                        {{ emailVerified ? 'mdi-email-check' : 'mdi-email-alert' }}
                      </v-icon>
                    </div>
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <!-- Quick Actions Card -->
        <v-card elevation="2" class="mb-6">
          <v-card-title class="d-flex align-center">
            <v-icon color="primary" class="mr-3">mdi-tune</v-icon>
            {{ $t('profile.quickActions.title') }}
          </v-card-title>
          
          <v-card-text>
            <v-row>
              <v-col cols="12">
                <v-btn
                  color="primary"
                  variant="tonal"
                  size="large"
                  prepend-icon="mdi-cog"
                  @click="goToSettings"
                  block
                >
                  {{ $t('profile.quickActions.goToSettings') }}
                </v-btn>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<style scoped>
.v-card {
  transition: transform 0.2s ease-in-out;
}

.v-card:hover {
  transform: translateY(-1px);
}
</style>